// Юридические лица, от которых может выставляться КП.
// Юрлицо с общим шаблоном КП (layout 'standard' — СПМ, ДИСТИНКТ) достаточно
// дописать сюда: реквизиты и логотип в PDF/Excel подтянутся сами. У МОСЭЛ свой
// бланк (layout 'mosel') — свой вид, то же содержание (kpTerms.ts); генераторы — kpMosel.ts,
// generateKpPdfMosel.ts, generateKpExcelMosel.ts; выбор — в downloadKp.ts.

export interface KpEntity {
  /** Стабильный идентификатор для селекта. */
  id: string
  /** Короткое имя для селекта и шапки таблицы: ООО «СПЕЦМАШЭЛЕКТРО». */
  shortName: string
  /** Полное наименование (для шапки документа). */
  fullName: string
  /** Город (короткая строка для карточки «Поставщик»). */
  city: string
  /** Реквизиты построчно (ИНН/КПП/ОГРН/адрес) — для PDF. */
  requisites: string[]
  /** Контакты построчно (тел/почта/сайт) — для PDF. */
  contacts: string[]
  /** Заголовок-блок названия компании в шапке Excel (многострочный). */
  excelTitle: string
  /** Реквизиты одним многострочным блоком в шапке Excel. */
  excelRequisites: string
  /** Логотип: путь в public/ + размер вставки в Excel (px). */
  logo: { url: string; width: number; height: number }
  /** Доступен ли PDF-формат. Если false — в модалке остаётся только Excel. */
  pdfEnabled: boolean
  /**
   * Раскладка документа: 'standard' (по умолчанию) — общий шаблон КП CRM,
   * 'mosel' — внешний вид бланка ООО «МосЭл»; содержание (итоги, условия,
   * исполнитель) — как у общего (решение 14.10.2026).
   */
  layout?: KpLayout
  /** Подписант внизу КП (бланк МосЭл) — строка печатается текстом, без подписи и печати. */
  signer?: { title: string; name: string }
}

export type KpLayout = 'standard' | 'mosel'

export const KP_ENTITIES: KpEntity[] = [
  {
    id: 'SPM',
    shortName: 'ООО «СПЕЦМАШЭЛЕКТРО»',
    fullName: 'Общество с ограниченной ответственностью «СПЕЦМАШЭЛЕКТРО»',
    city: 'г. Санкт-Петербург',
    requisites: [
      'ИНН 7813266433 · КПП 781401001 · ОГРН 1167847463046',
      '197101, г. Санкт-Петербург, Торфяная дорога, д. 7, лит. Ф, оф. 208',
    ],
    contacts: ['тел/факс: +7 (812) 245-38-01', 'zakaz@spm-electro.ru', 'spm-electro.ru'],
    excelTitle:
      'Общество с ограниченной ответственностью\n«СПЕЦМАШЭЛЕКТРО»\n(ООО «СПЕЦМАШЭЛЕКТРО»)',
    excelRequisites:
      'ИНН:7813266433, КПП: 781401001\nОГРН: 1167847463046\n197101, г. Санкт-Петербург, Торфяная дорога, дом7, литера Ф, офис 208\nE-mail: zakaz@spm-electro.ru\nTel/fax: +7 (812) 245-38-01',
    logo: { url: '/logo-spm.png', width: 150, height: 75 },
    pdfEnabled: true,
  },
  {
    id: 'DISTINCT',
    shortName: 'ООО «ДИСТИНКТ»',
    fullName: 'Общество с ограниченной ответственностью «ДИСТИНКТ»',
    city: 'г. Санкт-Петербург',
    requisites: [
      'ОГРН 1247800041917 · ИНН 7814837898',
      '197374, г. Санкт-Петербург, вн.тер. г. Муниципальный округ Озеро Долгое, дор. Торфяная, д. 7, литера Ф, помещ./кабинет 9-Н/22, офис 303',
    ],
    contacts: ['тел/факс: +7 (812) 245-38-01', 'zakaz@distinct.ru'],
    excelTitle: 'Общество с ограниченной ответственностью\n«ДИСТИНКТ»\n(ООО «ДИСТИНКТ»)',
    excelRequisites:
      'ОГРН 1247800041917  ИНН 7814837898\n197374, г. Санкт-Петербург, вн.тер. г. Муниципальный округ Озеро Долгое, дор. Торфяная, д.7, литера Ф, помещ./кабинет 9-Н/22, офис 303\nE-mail: zakaz@distinct.ru\nTel/fax: +7 (812) 245-38-01',
    // Логотип широкоформатный (1755×469, ~3.74:1) — масштабируем по ширине.
    logo: { url: '/logo-distinct.png', width: 172, height: 46 },
    // PDF-шаблон для ДИСТИНКТ ещё не сделан — пока только Excel.
    pdfEnabled: false,
  },
  {
    // Бланк «КП МосЭл» от РОЗ (25.09.2026): реквизиты — строки шапки бланка
    // как есть (убран только лишний пробел в «К. 2 ,ПОМЕЩ.»). Телефона, почты
    // и сайта в бланке нет — contacts пуст, пока заказчик их не пришлёт.
    id: 'MOSEL',
    shortName: 'ООО «МосЭл»',
    fullName: 'Общество с ограниченной ответственностью «МосЭл»',
    city: 'г. Москва',
    requisites: [
      'ИНН/КПП 7733464038/771601001',
      'ОГРН 1257700072244 ОКПО 73825859',
      'р/с 40702810040710001178 в ПАО «Сбербанк»',
      'г. Москва, БИК 044525225, к/с 30101810400000000225',
      'Адрес: 129347, Г.МОСКВА, ВН.ТЕР.Г. МУНИЦИПАЛЬНЫЙ ОКРУГ ЯРОСЛАВСКИЙ, Ш ЯРОСЛАВСКОЕ, Д.146 К. 2, ПОМЕЩ. 1/5.',
    ],
    contacts: [],
    excelTitle: 'Общество с ограниченной ответственностью\n«МосЭл»\n(ООО «МосЭл»)',
    excelRequisites:
      'ИНН/КПП 7733464038/771601001\nОГРН 1257700072244 ОКПО 73825859\nр/с 40702810040710001178 в ПАО «Сбербанк»\nг. Москва, БИК 044525225, к/с 30101810400000000225\nАдрес: 129347, Г.МОСКВА, ВН.ТЕР.Г. МУНИЦИПАЛЬНЫЙ ОКРУГ ЯРОСЛАВСКИЙ, Ш ЯРОСЛАВСКОЕ, Д.146 К. 2, ПОМЕЩ. 1/5.',
    // Логотип из бланка (468×210): в бланке он 82×37 мм, в Excel — те же 310×139 px.
    logo: { url: '/logo-mosel.png', width: 310, height: 139 },
    pdfEnabled: true,
    layout: 'mosel',
    signer: { title: 'Генеральный директор', name: 'Рабчук О. С.' },
  },
]

export const DEFAULT_KP_ENTITY = KP_ENTITIES[0]

// Телефон по умолчанию для строки «Телефон» в КП, если у ответственного
// менеджера он не заполнен в профиле. Общий номер компании.
export const DEFAULT_KP_PHONE = '+7 (812) 245-38-01'

export function getKpEntity(id: string): KpEntity {
  return KP_ENTITIES.find((e) => e.id === id) ?? DEFAULT_KP_ENTITY
}
